import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, ShieldAlert } from 'lucide-react'
import { rolesApi } from '@/api/rolesApi'
import { tenancySetupApi, type TenancySetupResult } from '@/api/tenancySetupApi'
import { tenantsApi } from '@/api/tenantsApi'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

/**
 * TEMPORARY adoption tooling (tenancy rollout): one-time association of all
 * legacy (NULL-tenant) data and membership-less users with a chosen tenant.
 * Deleted together with the backend module once every environment shows the
 * green all-zero state.
 */

const TABLE_LABELS: Record<string, string> = {
  formDefinition: 'Form definitions',
  procedureTemplate: 'Procedure templates',
  libraryField: 'Library fields',
  optionSet: 'Option sets',
  fieldCategory: 'Field categories',
  patient: 'Patients',
  procedure: 'Procedures',
  formSubmission: 'Form submissions',
  instrumentCorrelation: 'Instrument correlations',
  instrumentResult: 'Instrument results',
  dinTicket: 'DIN tickets',
  processStepConnection: 'Process step connections',
  processStepExecution: 'Process step executions',
  webhookNotification: 'Webhook deliveries',
  webhookSubscription: 'Webhook subscriptions',
  instrumentEventResult: 'Instrument event results',
  patientAudit: 'Patient audit entries',
}

export function TenancySetupSettings() {
  const qc = useQueryClient()
  const [tenantId, setTenantId] = useState('')
  const [defaultRoleId, setDefaultRoleId] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [lastResult, setLastResult] = useState<TenancySetupResult | null>(null)

  const statusQuery = useQuery({ queryKey: ['tenancy-setup-status'], queryFn: tenancySetupApi.status })
  const tenantsQuery = useQuery({ queryKey: ['tenants'], queryFn: tenantsApi.list })
  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: rolesApi.list })

  const allTenants = useMemo(() => tenantsQuery.data ?? [], [tenantsQuery.data])
  const selectedTenant = allTenants.find((t) => t.id === tenantId)
  const roles = useMemo(() => rolesQuery.data ?? [], [rolesQuery.data])
  const selectedRole = roles.find((r) => r.id === defaultRoleId)

  // Default to the least-privileged system role so a mis-click cannot hand out
  // admin across the board.
  useEffect(() => {
    if (defaultRoleId || roles.length === 0) return
    setDefaultRoleId((roles.find((r) => r.slug === 'user') ?? roles[roles.length - 1]).id)
  }, [roles, defaultRoleId])

  const applyMutation = useMutation({
    mutationFn: () => tenancySetupApi.apply(tenantId, defaultRoleId),
    onSuccess: (result) => {
      setLastResult(result)
      setConfirmOpen(false)
      // Legacy rows just became visible under the target tenant — every
      // cached list in the app is potentially stale.
      void qc.invalidateQueries()
    },
  })

  const status = statusQuery.data
  const nonZero = Object.entries(status?.unassigned ?? {}).filter(([, count]) => count > 0)
  // Role-less memberships count as "not done": their holders have no permissions
  // in the tenant, which is exactly the lockout this page exists to repair.
  const allDone =
    status !== undefined &&
    status.totalUnassigned === 0 &&
    status.usersWithoutMembership === 0 &&
    status.membershipsWithoutRole === 0

  if (statusQuery.isLoading) {
    return <p className='text-sm text-muted-foreground'>Loading unassigned-data counts…</p>
  }
  if (statusQuery.isError) {
    return (
      <p className='text-sm text-destructive'>
        Could not load tenancy-setup status: {(statusQuery.error as Error).message}
      </p>
    )
  }

  return (
    <div className='flex max-w-3xl flex-col gap-6'>
      <div>
        <h2 className='text-base font-semibold text-foreground'>Tenancy setup</h2>
        <p className='text-sm text-muted-foreground'>
          One-time adoption step: assigns all data that predates tenancy (and every user without a
          membership) to one tenant. This page disappears once nothing is left to assign.
        </p>
      </div>

      {allDone ? (
        <Card>
          <CardContent className='flex items-center gap-3 py-6'>
            <CheckCircle2 className='h-6 w-6 text-green-600' />
            <div>
              <p className='text-sm font-medium text-foreground'>Nothing unassigned</p>
              <p className='text-sm text-muted-foreground'>
                All data belongs to a tenant and every user has a membership. This page can be
                retired.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className='flex flex-col gap-3 py-4'>
              <div className='flex items-center gap-2'>
                <ShieldAlert className='h-5 w-5 text-amber-600' />
                <p className='text-sm font-medium text-foreground'>
                  {status?.totalUnassigned.toLocaleString()} unassigned records,{' '}
                  {status?.usersWithoutMembership.toLocaleString()} users without membership,{' '}
                  {status?.membershipsWithoutRole.toLocaleString()} memberships without a role
                </p>
              </div>
              <ul className='grid grid-cols-2 gap-x-8 gap-y-1'>
                {nonZero.map(([table, count]) => (
                  <li key={table} className='flex justify-between text-sm text-muted-foreground'>
                    <span>{TABLE_LABELS[table] ?? table}</span>
                    <span className='tabular-nums'>{count.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <div className='flex flex-col gap-4'>
            <div className='grid grid-cols-2 gap-4'>
              <div className='flex flex-col gap-1.5'>
                <Label>Tenant to assign everything to</Label>
                <SearchableSelect
                  value={tenantId}
                  onChange={setTenantId}
                  options={allTenants.map((t) => ({ value: t.id, label: t.name }))}
                  placeholder='Select a tenant'
                />
              </div>
              <div className='flex flex-col gap-1.5'>
                <Label>Role every member receives there</Label>
                <SearchableSelect
                  value={defaultRoleId}
                  onChange={setDefaultRoleId}
                  options={roles.map((r) => ({ value: r.id, label: r.displayName }))}
                  placeholder='Select a role'
                />
                <p className='text-xs text-muted-foreground'>
                  Required: a membership with no role grants no permissions at all.
                </p>
              </div>
            </div>
            <div>
              <Button
                disabled={!selectedTenant || !selectedRole}
                onClick={() => setConfirmOpen(true)}
              >
                Assign all…
              </Button>
            </div>
          </div>
        </>
      )}

      {lastResult && (
        <Card>
          <CardContent className='py-4'>
            <p className='text-sm text-foreground'>
              Assigned{' '}
              {Object.values(lastResult.assigned)
                .reduce((sum, n) => sum + n, 0)
                .toLocaleString()}{' '}
              records, created {lastResult.membershipsCreated.toLocaleString()} memberships and
              gave {lastResult.rolesBackfilled.toLocaleString()} role-less memberships a role.
            </p>
          </CardContent>
        </Card>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign everything to {selectedTenant?.name}?</DialogTitle>
            <DialogDescription>
              {status?.totalUnassigned.toLocaleString()} unassigned records will be assigned to{' '}
              {selectedTenant?.name}, and all {status?.usersWithoutMembership.toLocaleString()} users
              without a membership (plus everyone else) become members of it as{' '}
              <strong>{selectedRole?.displayName}</strong>. Any membership that currently has no
              role also gets that role. This runs in one transaction and can be re-run safely.
            </DialogDescription>
          </DialogHeader>
          {applyMutation.isError && (
            <p className='text-sm text-destructive'>{(applyMutation.error as Error).message}</p>
          )}
          <DialogFooter>
            <Button variant='outline' onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => applyMutation.mutate()} disabled={applyMutation.isPending}>
              {applyMutation.isPending ? 'Assigning…' : 'Assign all'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
